import classNames from 'classnames'
import classnames from 'classnames'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import { ButtonVariants } from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import Ctas from '~/components/atoms/Ctas/Ctas'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Image from '~/components/atoms/Image/Image'
import type { TeamMemberCardProps } from '~/components/atoms/TeamMemberCard/TeamMemberCard'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { ThemeType } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import { ButtonVariants as EnumButtonVariants } from '~/enums/buttonVariants'
import firstLettersConcatenated from '~/utils/firstLetterConcatenated'

import styles from './ExpertQuote.module.css'

export interface ExpertQuoteProps {
  testID?: string
  quote: PortableTextComponentProps['blocks']
  teamMember?: TeamMemberCardProps
  moduleId?: string
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  theme: ComponentTheme
  variant?: ThemeType
  isOnArticle?: boolean
}

export default function ExpertQuote({
  testID,
  quote,
  teamMember,
  moduleId,
  verticalPadding = false,
  noPaddingBottom,
  theme,
  variant,
  isOnArticle,
}: ExpertQuoteProps) {
  const primaryCta = (
    <div className={styles.ctaWrapper}>
      <Ctas
        ctas={[
          {
            linkText: `Request an appraisal`,
            launchForm: FormTypes.masterpiece,
            buttonStyle: EnumButtonVariants.primary,
            variant: ButtonVariants.primaryDark,
            children: undefined,
          },
        ]}
        theme={ComponentTheme.secondary}
      />
    </div>
  )

  return (
    <Fixture
      className={classNames(styles.base)}
      data-testid={testID}
      id={moduleId}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      theme={theme}
    >
      <AnimateIntoView type={AnimationType.fadeIn}>
        <Container>
          <div
            className={classNames(styles.contentContainer, {
              [styles.isOnArticle]: isOnArticle,
            })}
          >
            <div className={styles.content}>
              <div
                className={classNames(styles.quote, {
                  [styles.isOnArticle]: isOnArticle,
                })}
              >
                <BlockContent
                  value={quote}
                  size={TextSizes.magPrimaryTitleMed}
                />
              </div>
              {teamMember && (
                <div className={styles.teamBio}>
                  <div className={styles.teamBioContentWrapper}>
                    <div
                      className={classnames(
                        styles.avatar,
                        !teamMember?.image?.asset && styles.avatarFallback
                      )}
                    >
                      {teamMember?.image?.asset ? (
                        <Image
                          asset={teamMember.image.asset}
                          alt={teamMember.name}
                          width={60}
                        />
                      ) : (
                        <div className={styles.intialsFallback}>
                          <Text size={TextSizes.secondaryTitleMicro}>
                            {(teamMember.name &&
                              firstLettersConcatenated(teamMember.name)) ||
                              'MA'}
                          </Text>
                        </div>
                      )}
                    </div>
                    <div className={styles.teamBioContent}>
                      {teamMember?.name && (
                        <Text size={TextSizes.primaryTitleSml}>
                          {teamMember?.name},
                        </Text>
                      )}
                      {teamMember?.jobTitle && (
                        <Text size={TextSizes.primaryTitleSml}>
                          {teamMember?.jobTitle},
                        </Text>
                      )}
                      <Text size={TextSizes.primaryTitleSml}>
                        <em>MY</em>Artbroker
                      </Text>
                    </div>
                  </div>
                </div>
              )}
              {primaryCta}
            </div>
          </div>
        </Container>
      </AnimateIntoView>
    </Fixture>
  )
}
